/**
 * Lead qualification popup — trigger + open/close + screen navigation +
 * contact form submission to HubSpot + GA4 funnel tracking.
 *
 * WHAT THIS FILE DOES
 *   Decides WHEN the popup should open, handles opening/closing it, moves
 *   the visitor between its screens ("panels"), submits the contact form to
 *   HubSpot, and tracks the funnel for GA4.
 *
 * WHY NOT THE THEME'S Component/custom-element FRAMEWORK
 *   There is only ever one lead popup on a page, and it mostly needs
 *   page-level listeners (scroll, mouse, tab visibility) rather than
 *   per-instance lifecycle hooks. A small plain class keeps things
 *   organized without that extra framework overhead.
 *
 * SCREENS (each an element with [data-lead-popup-panel="name"])
 *   options -> survey -> recommendation -> contact -> thanks  ("Check my needs" path)
 *   options ---------------------------> contact -> thanks    ("Request callback" path,
 *                                                                survey answers are cleared)
 *   Only one panel is visible at a time (the rest have [hidden] set).
 *   A small history stack remembers where the visitor came from, so the
 *   same "back" button always returns to the right screen. Every time the
 *   popup opens, it is forced back to the "options" screen first — so it
 *   never resumes on a screen left over from an earlier visit to the page.
 *
 *   The survey's "View my recommendation" / "Get in touch" buttons only
 *   appear once every question has an answer, and are matched against the
 *   pop_up_qualification_recommended_result metaobject (JSON rendered by
 *   lead-popup-recommended-results-data.liquid) to fill in the recommendation
 *   screen's message + "View and order" link. Both button LABELS on the
 *   survey ("View my recommendation", "Get in touch") always come from
 *   Theme settings — they never change based on the matched rule.
 *
 *   The recommendation screen has 2 buttons, side by side: "View and order"
 *   (label + link both come from the matched rule's primary_label/primary_url,
 *   hidden entirely when that's blank) and "Request a callback" (label from
 *   Theme settings; reuses the same "go to contact" behaviour as the survey's
 *   own button). Both paths into the contact form are recorded in a hidden
 *   "entry_point" field, submitted to HubSpot together with the
 *   recommendation message/URL for context — see ENTRY_POINT_LABELS below
 *   for the exact values.
 *
 * CODE LAYOUT (inside the LeadPopup class, in order)
 *   1. Click/change dispatch  — #handleRootClick, #handleDialogCancel, #handleSurveyChange, #handleEmailInput
 *   2. Screen navigation      — #showPanel, #goBack, #showContact
 *   3. Survey + recommendation — #clearSurveyAnswers, #isSurveyComplete,
 *                                 #findMatchingRecommendation, #updateSurveyActions, #showRecommendation
 *   4. Contact form + HubSpot — #handleSubmit, #validateEmail, #submitToHubSpot
 *   5. Trigger detection + lifecycle — scroll/exit-intent/timer, #open, #close
 *   6. Event wiring           — #bindEvents, #unbindTriggers
 *
 * WHEN THE POPUP OPENS (whichever happens first)
 *   1. Visitor scrolls past a configured % of the page
 *   2. Visitor is viewing their 2nd page in this browser session
 *   3. Exit intent — desktop pointer devices only (mouse leaves the top of the page)
 *   4. Fallback: a configured amount of active tab time passes, used only
 *      if none of the above already happened
 *   A short minimum active time always applies first, so the popup can
 *   never appear the instant a page loads.
 *
 * GA4 (dataLayer + Shopify.analytics.publish — see the `track` helper below)
 *   lead_popup_view                     — popup opens (+ trigger reason)
 *   lead_popup_questionnaire_start      — survey screen shown, once per popup open
 *   lead_popup_questionnaire_complete   — every question answered, once per popup open
 *   lead_popup_recommendation_displayed — recommendation screen shown (+ selection), once per popup open
 *   lead_popup_contact_request          — contact screen shown (+ entry_point: "Check my needs" or "Request a callback"), every time
 *   lead_popup_email_submitted          — HubSpot submit succeeded (+ form_id, entry_point)
 *   lead_popup_close                    — popup closed (+ close_reason)
 *   purchase — NOT tracked here; see docs/lead-popup.md for the Shopify
 *   custom pixel that maps `checkout_completed` to a GA4 `purchase` event.
 *
 * STORAGE
 *   localStorage "leadPopupDismissedAt" — timestamp (ms) of the last time
 *   the popup was closed. Checked on load: if it's more recent than the
 *   configured "frequencyDays", the popup does not run at all.
 *   localStorage (not cookies) because this is a purely client-side
 *   "don't show again for a while" flag: it doesn't need to be sent to the
 *   server, and it persists across browser sessions (unlike sessionStorage).
 *
 *   localStorage "leadPopupSubmittedAt" — set once the contact form is
 *   successfully submitted. Unlike the dismissed flag, this is permanent:
 *   a visitor who already submitted never sees the popup again on this device.
 *
 *   sessionStorage "leadPopupPageViews" — number of pages viewed in the
 *   current browser tab. sessionStorage on purpose: it must reset for a new
 *   visit/tab, which is exactly what "session" storage is for.
 */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-lead-popup]';
  const DIALOG_SELECTOR = '[data-lead-popup-dialog]';
  const CLOSE_SELECTOR = '[data-lead-popup-close]';

  // Screen navigation.
  const PANEL_SELECTOR = '[data-lead-popup-panel]';
  const BACK_SELECTOR = '[data-lead-popup-back]';
  const STEP_SELECTOR = '[data-lead-popup-step]';
  const CALLBACK_ACTION_SELECTOR = '[data-lead-popup-action="callback"]';
  const VIEW_RECOMMENDATION_SELECTOR = '[data-lead-popup-result="view-solution"]';
  const SURVEY_GET_IN_TOUCH_SELECTOR = '[data-lead-popup-result="request-callback"]';

  // Survey completion + recommendation matching.
  const QUESTION_RADIO_SELECTOR = '[data-lead-popup-question-radio]';
  const QUESTIONS_ACTIONS_SELECTOR = '[data-lead-popup-questions-actions]';
  const RECOMMENDATION_MESSAGE_SELECTOR = '[data-lead-popup-recommendation-message]';
  const VIEW_ORDER_LINK_SELECTOR = '[data-lead-popup-result="view-order"]';
  const VIEW_ORDER_LABEL_SELECTOR = '[data-lead-popup-view-order-label]';
  const RECOMMENDATION_DATA_SELECTOR = '#lead-popup-recommended-results-data';

  // Contact form + HubSpot submit.
  const FORM_SELECTOR = '#lead-popup-form';
  const EMAIL_INPUT_SELECTOR = '[data-lead-popup-email]';
  const EMAIL_ERROR_SELECTOR = '[data-lead-popup-email-error]';
  const SUBMIT_ERROR_SELECTOR = '[data-lead-popup-submit-error]';
  const SUBMIT_BUTTON_SELECTOR = '[data-lead-popup-submit]';
  const SUBMIT_LABEL_SELECTOR = '[data-lead-popup-submit-label]';
  const MARKETING_CHECKBOX_SELECTOR = '[data-lead-popup-marketing]';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Hidden context fields sent to HubSpot with the visible form fields.
  const HIDDEN_MESSAGE_SELECTOR = '[data-lead-popup-hidden-message]';
  const HIDDEN_RECOMMENDATION_URL_SELECTOR = '[data-lead-popup-hidden-recommendation-url]';
  const HIDDEN_SOURCE_SELECTOR = '[data-lead-popup-hidden-source]';

  const DISMISSED_STORAGE_KEY = 'leadPopupDismissedAt';
  const SUBMITTED_STORAGE_KEY = 'leadPopupSubmittedAt';
  const PAGE_VIEWS_STORAGE_KEY = 'leadPopupPageViews';

  // How the visitor reached the contact form (screen 4), sent to HubSpot as
  // the "entry_point" hidden field and to GA4 as the entry_point param.
  // "callback" = skipped the questionnaire from screen 1's other option.
  // "survey" = went through the questionnaire first (via the survey's own
  // "Get in touch" button, or the recommendation screen's "Request a
  // callback" button — both count as the same entry point).
  /** @type {Record<string, string>} */
  const ENTRY_POINT_LABELS = {
    callback: 'Request a callback',
    survey: 'Check my needs',
  };

  // GA4 funnel events — see the top-of-file doc comment for what each one means.
  const EVENTS = {
    VIEW: 'lead_popup_view',
    QUESTIONNAIRE_START: 'lead_popup_questionnaire_start',
    QUESTIONNAIRE_COMPLETE: 'lead_popup_questionnaire_complete',
    RECOMMENDATION_DISPLAYED: 'lead_popup_recommendation_displayed',
    CONTACT_REQUEST: 'lead_popup_contact_request',
    EMAIL_SUBMITTED: 'lead_popup_email_submitted',
    CLOSE: 'lead_popup_close',
  };

  // Never open the popup before this much active time has passed, even if a
  // trigger condition is already met (e.g. a very short page, or a visitor
  // who lands directly on their 2nd page view).
  const MIN_ACTIVE_MS = 5000;

  /**
   * @typedef {Object} LeadPopupConfig
   * @property {number} fallbackDelayMs - Fallback trigger delay, in ms of active tab time.
   * @property {number} frequencyDays - Days to hide the popup again after it's dismissed.
   * @property {number} scrollThreshold - Scroll depth percentage (0-100) that triggers the popup.
   */

  /**
   * Reads and validates the popup's `data-config` JSON attribute.
   * Never throws — falls back to sensible defaults if the attribute is
   * missing or invalid.
   * @param {HTMLElement} root
   * @returns {LeadPopupConfig}
   */
  const readConfig = (root) => {
    /** @type {{ fallbackDelaySeconds?: number, frequencyDays?: number, scrollThreshold?: number }} */
    let raw = {};

    try {
      raw = JSON.parse(root.getAttribute('data-config') || '{}');
    } catch (error) {
      raw = {};
    }

    return {
      fallbackDelayMs: (Number(raw.fallbackDelaySeconds) || 90) * 1000,
      frequencyDays: Number(raw.frequencyDays) || 30,
      scrollThreshold: Number(raw.scrollThreshold) || 60,
    };
  };

  /** True on devices that have a real mouse (used to gate exit-intent). */
  const isDesktopPointerDevice = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /**
   * Increments and returns the page-view count for the current browser
   * session (tab). Used for the "2nd page view" trigger.
   * @returns {number}
   */
  const countSessionPageView = () => {
    try {
      const count = Number(sessionStorage.getItem(PAGE_VIEWS_STORAGE_KEY) || '0') + 1;
      sessionStorage.setItem(PAGE_VIEWS_STORAGE_KEY, String(count));
      return count;
    } catch (error) {
      return 1;
    }
  };

  /**
   * @param {number} frequencyDays
   * @returns {boolean} True if the popup was dismissed too recently to show again.
   */
  const isDismissedRecently = (frequencyDays) => {
    try {
      const dismissedAt = Number(localStorage.getItem(DISMISSED_STORAGE_KEY) || '0');
      if (!dismissedAt) return false;

      const frequencyMs = frequencyDays * 24 * 60 * 60 * 1000;
      return Date.now() - dismissedAt < frequencyMs;
    } catch (error) {
      return false;
    }
  };

  /** Remembers "now" as the last dismissed time, so the popup waits before showing again. */
  const rememberDismissed = () => {
    try {
      localStorage.setItem(DISMISSED_STORAGE_KEY, String(Date.now()));
    } catch (error) {
      // Storage may be unavailable (private browsing, disabled, full) — fail silently.
    }
  };

  /** @returns {boolean} True if this visitor already submitted the contact form before. */
  const hasSubmittedBefore = () => {
    try {
      return Boolean(localStorage.getItem(SUBMITTED_STORAGE_KEY));
    } catch (error) {
      return false;
    }
  };

  /** Marks the form as submitted, so the popup never shows again on this device. */
  const rememberSubmitted = () => {
    try {
      localStorage.setItem(SUBMITTED_STORAGE_KEY, String(Date.now()));
    } catch (error) {
      // Storage may be unavailable (private browsing, disabled, full) — fail silently.
    }
  };

  /**
   * @param {string} name
   * @returns {string} Cookie value, or '' if not found.
   */
  const readCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match && match[1] ? decodeURIComponent(match[1]) : '';
  };

  /**
   * Pushes a lead-popup funnel event to GA4 (via GTM's `dataLayer`) and to
   * Shopify's customer-events pipeline (via `Shopify.analytics.publish`,
   * for a custom pixel — see docs/lead-popup.md for the pixel-side setup).
   * @param {string} eventName
   * @param {Record<string, string | undefined>} [detail]
   */
  const track = (eventName, detail) => {
    /** @type {Window & { dataLayer?: Record<string, unknown>[], Shopify?: { analytics?: { publish?: Function } } }} */
    const win = window;
    win.dataLayer = win.dataLayer || [];

    /** @type {Record<string, unknown>} */
    const payload = { event: eventName };
    /** @type {Record<string, string>} */
    const customData = {};

    if (detail) {
      Object.keys(detail).forEach((key) => {
        const value = detail[key];
        if (value == null || value === '') return;
        payload[key] = value;
        customData[key] = value;
      });
    }

    win.dataLayer.push(payload);

    try {
      if (win.Shopify && win.Shopify.analytics && typeof win.Shopify.analytics.publish === 'function') {
        win.Shopify.analytics.publish(eventName, customData);
      }
    } catch (error) {
      // Custom pixel not present/ready — safe to ignore, GTM already got the event above.
    }
  };

  /**
   * One row from the pop_up_qualification_recommended_result metaobject,
   * rendered as JSON by lead-popup-recommended-results-data.liquid.
   * @typedef {Object} LeadPopupRecommendation
   * @property {string} property - Radio `name` of the question this rule matches (e.g. daily users).
   * @property {string} selection - Radio `value` this rule matches.
   * @property {string} message - Recommendation HTML for the recommendation screen (metaobject rich text field `recommended_message`, rendered to HTML by Liquid).
   * @property {string} primary_label - Label for the "View and order" link.
   * @property {string} primary_url - "View and order" link target; button is hidden when blank.
   */

  /**
   * Reads and parses the recommendation rules JSON. Never throws — an empty
   * list just means no recommendation message/label gets filled in.
   * @param {HTMLElement} root
   * @returns {LeadPopupRecommendation[]}
   */
  const readRecommendations = (root) => {
    const script = root.querySelector(RECOMMENDATION_DATA_SELECTOR);
    if (!(script instanceof HTMLScriptElement)) return [];

    try {
      const data = JSON.parse(script.textContent || '[]');
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return [];
    }
  };

  /**
   * Converts the recommendation's rich text HTML (used for on-screen display)
   * into plain text with one line break per block/list item, for the
   * "recommendation_message" hidden field — HubSpot's property is plain text,
   * it shouldn't contain HTML tags.
   * @param {string} html
   * @returns {string}
   */
  const htmlToPlainText = (html) => {
    if (!html) return '';

    const container = document.createElement('div');
    container.innerHTML = html;
    container.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li, br').forEach((el) => {
      el.insertAdjacentText('afterend', '\n');
    });

    return (container.textContent || '').replace(/\n{2,}/g, '\n').trim();
  };

  /** Controls a single lead popup: when it opens, and how it closes. */
  class LeadPopup {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;

      const dialogEl = root.querySelector(DIALOG_SELECTOR);
      /** @type {HTMLDialogElement | null} */
      this.dialog = dialogEl instanceof HTMLDialogElement ? dialogEl : null;

      this.config = readConfig(root);

      this.hasOpened = false;
      this.activeMs = 0;
      this.lastTickAt = Date.now();
      this.isTabActive = document.visibilityState !== 'hidden';
      this.pageViews = countSessionPageView();
      this.isDesktopPointer = isDesktopPointerDevice();

      /** @type {number | null} */
      this.timerId = null;

      // "options" is the screen shown by default in the markup.
      /** @type {string} */
      this.currentPanel = 'options';
      /** @type {string[]} */
      this.panelHistory = [];

      this.recommendations = readRecommendations(root);
      /** @type {LeadPopupRecommendation | null} */
      this.matchedRecommendation = null;

      const formEl = root.querySelector(FORM_SELECTOR);
      /** @type {HTMLFormElement | null} */
      this.form = formEl instanceof HTMLFormElement ? formEl : null;

      // Which screen led the visitor to the contact form (see #showContact).
      /** @type {string} */
      this.contactSource = '';

      const submitButton = root.querySelector(SUBMIT_BUTTON_SELECTOR);
      this.submitLabel = submitButton ? submitButton.getAttribute('data-label-submit') || '' : '';
      this.submittingLabel = submitButton
        ? submitButton.getAttribute('data-label-submitting') || ''
        : '';

      // Each "once per popup open" GA4 event gets its own flag, reset in
      // #resetTracking() every time the popup opens (see #open).
      this.trackedQuestionnaireStart = false;
      this.trackedQuestionnaireComplete = false;
      this.trackedRecommendationDisplayed = false;
    }

    /** @returns {boolean} True when the required markup exists, so we can safely run. */
    isReady() {
      return this.dialog !== null;
    }

    /** Starts watching for a trigger, unless already submitted or the frequency cap is still active. */
    start() {
      if (!this.isReady()) return;
      if (hasSubmittedBefore() || isDismissedRecently(this.config.frequencyDays)) return;

      this.#bindEvents();
      this.timerId = window.setInterval(this.#handleTick, 1000);
      this.#checkScrollDepth();
    }

    // -----------------------------------------------------------------------
    // 1. Click / change dispatch
    // -----------------------------------------------------------------------

    /** @param {Event} event */
    #handleRootClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest(CLOSE_SELECTOR)) {
        event.preventDefault();
        this.#close('button');
        return;
      }

      if (target.closest(BACK_SELECTOR)) {
        this.#goBack();
        return;
      }

      // "Request to get in touch" on the options screen: skip the survey
      // entirely, and make sure no leftover radio answers are submitted.
      if (target.closest(CALLBACK_ACTION_SELECTOR)) {
        this.#clearSurveyAnswers();
        this.#showContact('callback');
        return;
      }

      // "View my recommendation" at the bottom of the survey.
      if (target.closest(VIEW_RECOMMENDATION_SELECTOR)) {
        this.#showRecommendation();
        return;
      }

      // "Get in touch"/"Request a callback": same button + behaviour on both
      // the survey and the recommendation screen — both came through the
      // questionnaire, so they share the same entry point.
      if (target.closest(SURVEY_GET_IN_TOUCH_SELECTOR)) {
        this.#showContact('survey');
        return;
      }

      // Any other explicit "go to this screen" button (e.g. "Check my needs").
      const stepButton = target.closest(STEP_SELECTOR);
      if (stepButton) {
        const panelName = stepButton.getAttribute('data-lead-popup-step');
        if (panelName) this.#showPanel(panelName);
        return;
      }

      // Click landed directly on the dialog element itself = backdrop click.
      if (target === this.dialog) this.#close('backdrop');
    };

    /**
     * Escape key: <dialog> fires a cancel event before closing itself.
     * @param {Event} event
     */
    #handleDialogCancel = (event) => {
      event.preventDefault();
      this.#close('escape');
    };

    /**
     * A survey radio changed: re-check completion and update the recommendation.
     * @param {Event} event
     */
    #handleSurveyChange = (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.matches(QUESTION_RADIO_SELECTOR)) {
        this.#updateSurveyActions();
      }
    };

    /** Clears the email field's error as soon as the visitor edits it. @param {Event} event */
    #handleEmailInput = (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.matches(EMAIL_INPUT_SELECTOR)) {
        this.#clearEmailError();
      }
    };

    // -----------------------------------------------------------------------
    // 2. Screen navigation
    // -----------------------------------------------------------------------

    /**
     * Shows one screen and hides the rest. Remembers the screen we came
     * from, so #goBack() can return to it later.
     * @param {string} panelName
     */
    #showPanel(panelName) {
      if (panelName === this.currentPanel) return;

      const panels = this.root.querySelectorAll(PANEL_SELECTOR);
      if (!panels.length) return;

      for (const panel of panels) {
        if (panel instanceof HTMLElement) {
          panel.hidden = panel.getAttribute('data-lead-popup-panel') !== panelName;
        }
      }

      this.panelHistory.push(this.currentPanel);
      this.currentPanel = panelName;

      if (panelName === 'survey' && !this.trackedQuestionnaireStart) {
        this.trackedQuestionnaireStart = true;
        track(EVENTS.QUESTIONNAIRE_START);
      }
    }

    /** Returns to whichever screen the visitor came from, if any. */
    #goBack() {
      const previousPanel = this.panelHistory.pop();
      if (!previousPanel) return;

      for (const panel of this.root.querySelectorAll(PANEL_SELECTOR)) {
        if (panel instanceof HTMLElement) {
          panel.hidden = panel.getAttribute('data-lead-popup-panel') !== previousPanel;
        }
      }

      this.currentPanel = previousPanel;
    }

    /**
     * Forces the popup back to its first ("options") screen and clears the
     * back-button history. Called every time the popup opens, so it always
     * starts on screen 1 — even if the browser restored an older DOM state
     * (e.g. back/forward cache) where a different screen was left showing.
     */
    #resetToOptionsPanel() {
      for (const panel of this.root.querySelectorAll(PANEL_SELECTOR)) {
        if (panel instanceof HTMLElement) {
          panel.hidden = panel.getAttribute('data-lead-popup-panel') !== 'options';
        }
      }

      this.currentPanel = 'options';
      this.panelHistory = [];
    }

    /** Resets the "once per popup open" GA4 flags, so the funnel is tracked fresh on every open. */
    #resetTracking() {
      this.trackedQuestionnaireStart = false;
      this.trackedQuestionnaireComplete = false;
      this.trackedRecommendationDisplayed = false;
    }

    /**
     * Shows the contact form, remembering which path sent the visitor
     * there (`source`) so it can be recorded as the entry_point sent to
     * HubSpot/GA4.
     * @param {'callback' | 'survey'} source - See ENTRY_POINT_LABELS above.
     */
    #showContact(source) {
      this.contactSource = source;
      this.#syncHiddenFields();
      this.#showPanel('contact');
      track(EVENTS.CONTACT_REQUEST, { entry_point: this.#entryPointLabel() });
    }

    /** @returns {string} Human-readable entry_point value for the current contact source. */
    #entryPointLabel() {
      return ENTRY_POINT_LABELS[this.contactSource] || '';
    }

    /** Fills the contact form's hidden context fields from the matched recommendation + contact source. */
    #syncHiddenFields() {
      const messageHtml = (this.matchedRecommendation && this.matchedRecommendation.message) || '';
      this.#setHiddenField(HIDDEN_MESSAGE_SELECTOR, htmlToPlainText(messageHtml));
      this.#setHiddenField(HIDDEN_RECOMMENDATION_URL_SELECTOR, (this.matchedRecommendation && this.matchedRecommendation.primary_url) || '');
      this.#setHiddenField(HIDDEN_SOURCE_SELECTOR, this.#entryPointLabel());
    }

    /**
     * @param {string} selector
     * @param {string} value
     */
    #setHiddenField(selector, value) {
      const field = this.root.querySelector(selector);
      if (field instanceof HTMLInputElement) field.value = value;
    }

    // -----------------------------------------------------------------------
    // 3. Survey completion + recommendation matching
    // -----------------------------------------------------------------------

    /**
     * Unchecks every survey radio, so a skipped questionnaire never sends
     * stale answers, and re-hides the "View my recommendation" / "Get in
     * touch" buttons since the survey is no longer complete.
     */
    #clearSurveyAnswers() {
      for (const radio of this.root.querySelectorAll(QUESTION_RADIO_SELECTOR)) {
        if (radio instanceof HTMLInputElement) radio.checked = false;
      }

      this.matchedRecommendation = null;
      this.#updateSurveyActions();
    }

    /** @returns {boolean} True once every question (radio group) has a selected answer. */
    #isSurveyComplete() {
      const radios = this.root.querySelectorAll(QUESTION_RADIO_SELECTOR);
      if (!radios.length) return false;

      const allGroups = new Set();
      const answeredGroups = new Set();

      for (const radio of radios) {
        if (!(radio instanceof HTMLInputElement)) continue;
        allGroups.add(radio.name);
        if (radio.checked) answeredGroups.add(radio.name);
      }

      return answeredGroups.size === allGroups.size;
    }

    /** @returns {LeadPopupRecommendation | null} The rule matching the currently checked answers, if any. */
    #findMatchingRecommendation() {
      for (const radio of this.root.querySelectorAll(QUESTION_RADIO_SELECTOR)) {
        if (!(radio instanceof HTMLInputElement) || !radio.checked) continue;

        const match = this.recommendations.find(
          (entry) => entry.property === radio.name && entry.selection === radio.value
        );
        if (match) return match;
      }

      return null;
    }

    /**
     * Shows/hides the "View my recommendation" / "Get in touch" buttons
     * together, and updates which recommendation rule matches the current
     * answers (used by #showRecommendation() right after).
     */
    #updateSurveyActions() {
      const actions = this.root.querySelector(QUESTIONS_ACTIONS_SELECTOR);
      if (!(actions instanceof HTMLElement)) return;

      const isComplete = this.#isSurveyComplete();
      actions.hidden = !isComplete;
      if (!isComplete) return;

      if (!this.trackedQuestionnaireComplete) {
        this.trackedQuestionnaireComplete = true;
        track(EVENTS.QUESTIONNAIRE_COMPLETE);
      }

      this.matchedRecommendation = this.#findMatchingRecommendation();
    }

    /**
     * Shows the recommendation screen, filled in with the matched rule's
     * message + "View and order" link (label and URL both come from the
     * metaobject) — that button stays hidden whenever the matched rule has
     * no primary_url.
     */
    #showRecommendation() {
      const message = this.root.querySelector(RECOMMENDATION_MESSAGE_SELECTOR);
      if (message instanceof HTMLElement) {
        // recommended_message is a Shopify rich text field — the Liquid side
        // already renders it to HTML, so it's set via innerHTML (trusted
        // merchant content from the metaobject, not user input).
        const html = (this.matchedRecommendation && this.matchedRecommendation.message) || '';
        message.innerHTML = html;
        message.hidden = html === '';
      }

      const viewOrderLink = this.root.querySelector(VIEW_ORDER_LINK_SELECTOR);
      if (viewOrderLink instanceof HTMLAnchorElement) {
        const url = (this.matchedRecommendation && this.matchedRecommendation.primary_url) || '';
        viewOrderLink.href = url || '#';
        viewOrderLink.hidden = url === '';

        const label = this.root.querySelector(VIEW_ORDER_LABEL_SELECTOR);
        if (label instanceof HTMLElement) {
          label.textContent = (this.matchedRecommendation && this.matchedRecommendation.primary_label) || '';
        }
      }

      this.#showPanel('recommendation');

      if (this.matchedRecommendation && this.matchedRecommendation.message && !this.trackedRecommendationDisplayed) {
        this.trackedRecommendationDisplayed = true;
        track(EVENTS.RECOMMENDATION_DISPLAYED, { selection: this.matchedRecommendation.selection });
      }
    }

    // -----------------------------------------------------------------------
    // 4. Contact form + HubSpot submit
    // -----------------------------------------------------------------------

    /** @param {SubmitEvent} event */
    #handleSubmit = (event) => {
      event.preventDefault();
      if (!this.form || !this.#validateEmail()) return;

      this.#setSubmitErrorVisible(false);
      this.#setSubmitLoading(true);

      this.#submitToHubSpot()
        .then(() => {
          rememberSubmitted();
          track(EVENTS.EMAIL_SUBMITTED, {
            form_id: this.form ? this.form.getAttribute('data-hubspot-form-id') || undefined : undefined,
            entry_point: this.#entryPointLabel(),
          });
          this.#showPanel('thanks');
        })
        .catch(() => {
          this.#setSubmitErrorVisible(true);
        })
        .finally(() => {
          this.#setSubmitLoading(false);
        });
    };

    /** @returns {boolean} True if the email field is valid (or missing from the DOM). */
    #validateEmail() {
      const emailInput = this.root.querySelector(EMAIL_INPUT_SELECTOR);
      if (!(emailInput instanceof HTMLInputElement)) return true;

      const errorEl = this.root.querySelector(EMAIL_ERROR_SELECTOR);
      const value = emailInput.value.trim();
      let message = '';

      if (!value) {
        message = errorEl instanceof HTMLElement ? errorEl.getAttribute('data-error-required') || '' : '';
      } else if (!EMAIL_PATTERN.test(value)) {
        message = errorEl instanceof HTMLElement ? errorEl.getAttribute('data-error-invalid') || '' : '';
      }

      if (!message) {
        this.#clearEmailError();
        return true;
      }

      emailInput.setAttribute('aria-invalid', 'true');
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
      emailInput.focus();
      return false;
    }

    /** Clears the email field's error state, e.g. as the visitor starts fixing it. */
    #clearEmailError() {
      const emailInput = this.root.querySelector(EMAIL_INPUT_SELECTOR);
      const errorEl = this.root.querySelector(EMAIL_ERROR_SELECTOR);
      if (emailInput instanceof HTMLInputElement) emailInput.removeAttribute('aria-invalid');
      if (errorEl instanceof HTMLElement) errorEl.hidden = true;
    }

    /** @param {boolean} visible */
    #setSubmitErrorVisible(visible) {
      const errorEl = this.root.querySelector(SUBMIT_ERROR_SELECTOR);
      if (errorEl instanceof HTMLElement) errorEl.hidden = !visible;
    }

    /** @param {boolean} isLoading */
    #setSubmitLoading(isLoading) {
      const submitButton = this.root.querySelector(SUBMIT_BUTTON_SELECTOR);
      const labelEl = this.root.querySelector(SUBMIT_LABEL_SELECTOR);
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = isLoading;
      if (labelEl instanceof HTMLElement) {
        labelEl.textContent = isLoading ? this.submittingLabel : this.submitLabel;
      }
    }

    /**
     * Submits the form to HubSpot's Forms API.
     * @returns {Promise<void>}
     */
    #submitToHubSpot() {
      const form = this.form;
      if (!(form instanceof HTMLFormElement)) return Promise.reject(new Error('Missing lead popup form'));

      const portalId = form.getAttribute('data-hubspot-portal-id') || '';
      const formId = form.getAttribute('data-hubspot-form-id') || '';
      if (!portalId || !formId) return Promise.reject(new Error('Missing HubSpot portal/form id'));

      /** @type {Record<string, unknown>} */
      const payload = {
        fields: this.#buildHubSpotFields(),
        context: {
          pageUri: window.location.href,
          pageName: document.title,
          hutk: readCookie('hubspotutk') || undefined,
        },
      };

      const legalConsentOptions = this.#buildLegalConsentOptions();
      if (legalConsentOptions) payload.legalConsentOptions = legalConsentOptions;

      const endpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${encodeURIComponent(portalId)}/${encodeURIComponent(formId)}`;

      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then((response) => {
        if (!response.ok) throw new Error('HubSpot submit failed');
      });
    }

    /** @returns {{ name: string, value: string }[]} Every non-empty form field, skipping the marketing checkbox (sent via legalConsentOptions instead). */
    #buildHubSpotFields() {
      /** @type {{ name: string, value: string }[]} */
      const fields = [];
      if (!(this.form instanceof HTMLFormElement)) return fields;

      const seen = new Set();
      new FormData(this.form).forEach((value, name) => {
        if (!name || seen.has(name) || name === 'marketing_opt_in') return;
        const stringValue = typeof value === 'string' ? value.trim() : '';
        if (!stringValue) return;

        seen.add(name);
        fields.push({ name, value: stringValue });
      });

      return fields;
    }

    /** @returns {Object | null} HubSpot legalConsentOptions, or null if no subscription type is configured. */
    #buildLegalConsentOptions() {
      const form = this.form;
      if (!(form instanceof HTMLFormElement)) return null;

      const subscriptionTypeId = Number(form.getAttribute('data-hubspot-subscription-type-id'));
      if (!subscriptionTypeId) return null;

      const marketingInput = form.querySelector(MARKETING_CHECKBOX_SELECTOR);
      const marketingOptedIn = marketingInput instanceof HTMLInputElement && marketingInput.checked;

      return {
        consent: {
          consentToProcess: true,
          text: form.getAttribute('data-hubspot-consent-process-text') || '',
          communications: [
            {
              value: marketingOptedIn,
              subscriptionTypeId,
              text: form.getAttribute('data-hubspot-consent-marketing-text') || '',
            },
          ],
        },
      };
    }

    // -----------------------------------------------------------------------
    // 5. Trigger detection + open/close lifecycle
    // -----------------------------------------------------------------------

    #checkScrollDepth = () => {
      if (this.hasOpened || this.activeMs < MIN_ACTIVE_MS) return;

      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolledPercent = scrollableHeight > 0 ? (window.scrollY / scrollableHeight) * 100 : 100;

      if (scrolledPercent >= this.config.scrollThreshold) this.#attemptOpen('scroll');
    };

    /** @param {MouseEvent} event */
    #handleExitIntent = (event) => {
      if (this.hasOpened || this.activeMs < MIN_ACTIVE_MS) return;
      if (event.clientY <= 0) this.#attemptOpen('exit_intent');
    };

    /** Runs every second while the tab is active: tracks active time and checks time-based triggers. */
    #handleTick = () => {
      if (!this.isTabActive || this.hasOpened) return;

      const now = Date.now();
      this.activeMs += now - this.lastTickAt;
      this.lastTickAt = now;

      if (this.activeMs < MIN_ACTIVE_MS) return;

      // 2nd page view is its own trigger — no need to wait for the fallback delay.
      if (this.pageViews >= 2) {
        this.#attemptOpen('second_page');
        return;
      }

      if (this.activeMs >= this.config.fallbackDelayMs) this.#attemptOpen('delay');
    };

    /** Pauses active-time tracking while the tab is in the background. */
    #handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.isTabActive = false;
        return;
      }

      this.isTabActive = true;
      this.lastTickAt = Date.now();
    };

    /**
     * Opens the popup once, after the current trigger has actually been met.
     * @param {string} reason - Which trigger fired, forwarded to #open() for GA4.
     */
    #attemptOpen(reason) {
      if (this.hasOpened) return;

      // Wait a couple of frames so the dialog opens smoothly, not mid-scroll/mid-paint.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => this.#open(reason));
      });
    }

    /** @param {string} [reason] - Trigger that opened the popup, e.g. 'scroll', for GA4. */
    #open(reason) {
      const dialog = this.dialog;
      if (this.hasOpened || !dialog) return;
      this.hasOpened = true;

      // Trigger listeners are no longer needed once the popup is open.
      this.#unbindTriggers();

      // Always start on screen 1, regardless of whatever screen was
      // showing the last time this markup was rendered.
      this.#resetToOptionsPanel();
      this.#resetTracking();

      this.root.hidden = false;
      dialog.showModal();
      track(EVENTS.VIEW, { trigger: reason });
    }

    /** @param {string} [reason] - Why the popup closed, e.g. 'button'/'backdrop'/'escape', for GA4. */
    #close(reason) {
      const dialog = this.dialog;
      if (!dialog || !dialog.open) return;

      dialog.close();
      this.root.hidden = true;
      rememberDismissed();
      track(EVENTS.CLOSE, { close_reason: reason });
    }

    // -----------------------------------------------------------------------
    // 6. Event wiring
    // -----------------------------------------------------------------------

    #bindEvents() {
      const dialog = this.dialog;
      if (!dialog) return;

      this.root.addEventListener('click', this.#handleRootClick);
      this.root.addEventListener('change', this.#handleSurveyChange);
      dialog.addEventListener('cancel', this.#handleDialogCancel);

      if (this.form) {
        this.form.addEventListener('submit', this.#handleSubmit);
        this.form.addEventListener('input', this.#handleEmailInput);
      }

      document.addEventListener('scroll', this.#checkScrollDepth, { passive: true });
      if (this.isDesktopPointer) document.addEventListener('mousemove', this.#handleExitIntent);
      document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    /** Removes only the trigger-watching listeners; close/escape stay active while open. */
    #unbindTriggers() {
      if (this.timerId) window.clearInterval(this.timerId);
      document.removeEventListener('scroll', this.#checkScrollDepth);
      document.removeEventListener('mousemove', this.#handleExitIntent);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }
  }

  for (const root of document.querySelectorAll(ROOT_SELECTOR)) {
    if (root instanceof HTMLElement) new LeadPopup(root).start();
  }
})();
